import { type UIMessage } from 'ai'
import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { AttachmentBar, BetaBadge } from '@avantstay/backoffice-core'

import * as S from './AskAiChatSidePanel.styles'
import { RemyChat } from './RemyChat'
import { fetchRemySessionMessages, fetchRemySessions, RemySession } from './remySessions'
import { OnboardingGate, SessionGate, SessionPicker } from './SessionViews'

type SessionView = 'loading' | 'onboarding' | 'gate' | 'picker' | 'chat'

const PANEL_DEFAULT_WIDTH = 520
const PANEL_MIN_WIDTH = 420
const PANEL_MAX_WIDTH = 960

interface AskAiChatSidePanelProps {
  // The component stays mounted while closed (the launcher keeps it around for
  // the whole tab session) so the chat keeps its context; `open` only controls
  // whether the panel bar is on screen.
  open: boolean
  onClose: () => void
}

export const AskAiChatSidePanel = ({ open, onClose }: AskAiChatSidePanelProps) => {
  const [view, setView] = useState<SessionView>('loading')
  const [sessions, setSessions] = useState<RemySession[]>([])
  const [sessionId, setSessionId] = useState('')
  const [initialMessages, setInitialMessages] = useState<UIMessage[] | undefined>(undefined)
  const [resumingId, setResumingId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [connectUrl, setConnectUrl] = useState<string | null>(null)
  const [checkingOnboarding, setCheckingOnboarding] = useState(false)
  const connectPopupRef = useRef<Window | null>(null)

  // Persistent DOM host for the chat views. They render into it through a
  // portal, so closing the panel merely detaches this element from the
  // document — the React subtree (messages, input, attachments, an in-flight
  // response) lives on until the tab unloads. `display: contents` keeps the
  // views laying out as direct children of the panel container.
  const [chatHost] = useState(() => {
    const host = document.createElement('div')
    host.style.display = 'contents'
    return host
  })

  // Adopt the persistent chat subtree into a freshly opened panel. Re-attached
  // DOM loses its scroll offsets, so overflowing areas (the message log) are
  // sent back to their latest content.
  const adoptChatHost = useCallback(
    (slot: HTMLDivElement | null) => {
      if (!slot) return
      slot.appendChild(chatHost)
      requestAnimationFrame(() => {
        chatHost.querySelectorAll('div').forEach(el => {
          if (el.scrollHeight > el.clientHeight) el.scrollTop = el.scrollHeight
        })
      })
    },
    [chatHost],
  )

  const startNewChat = useCallback(() => {
    setInitialMessages(undefined)
    setSessionId(crypto.randomUUID())
    setLoadError(null)
    setView('chat')
  }, [])

  // Route to the right view for a fresh session-list response: connect flow
  // first when Remy says the Voyage connector is missing, then continue/new.
  const enterFromSessionList = useCallback(
    (list: RemySession[], onboarding: { required: boolean; connectUrl?: string }) => {
      setSessions(list)
      if (onboarding.required && onboarding.connectUrl) {
        setConnectUrl(onboarding.connectUrl)
        setView('onboarding')
        return
      }
      if (list.length > 0) setView('gate')
      else startNewChat()
    },
    [startNewChat],
  )

  // On open, load the most recent sessions. Un-onboarded users get the connect
  // flow; users with prior chats may continue or start fresh; everyone else
  // drops straight into a new chat.
  useEffect(() => {
    let cancelled = false
    fetchRemySessions(3)
      .then(({ sessions: list, onboarding }) => {
        if (cancelled) return
        enterFromSessionList(list, onboarding)
      })
      .catch(() => {
        // Listing failed — don't block the user, just start chatting.
        if (!cancelled) startNewChat()
      })
    return () => {
      cancelled = true
    }
  }, [enterFromSessionList, startNewChat])

  // "I've connected" on the onboarding view: re-ask Remy. Still not connected
  // keeps the connect view (with a fresh link); connected proceeds as usual.
  const recheckOnboarding = useCallback(() => {
    setCheckingOnboarding(true)
    fetchRemySessions(3)
      .then(({ sessions: list, onboarding }) => enterFromSessionList(list, onboarding))
      .catch(() => {})
      .finally(() => setCheckingOnboarding(false))
  }, [enterFromSessionList])

  // Open the connect flow in a popup over Voyage so completing it flows the
  // user straight back into the chat. A blocked popup falls through to the
  // anchor's regular new-tab behavior.
  const openConnectPopup = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!connectUrl) return
      const popup = window.open(connectUrl, 'remy-connect', 'popup=yes,width=560,height=720')
      if (!popup) return
      event.preventDefault()
      connectPopupRef.current = popup
    },
    [connectUrl],
  )

  // Watch the connect popup: the success page posts `remy:connector-connected`
  // (origin-checked) and the panel closes the popup and continues to the chat
  // automatically. A popup closed without the signal (abort, or the flow
  // finished before the listener attached) triggers the same recheck.
  useEffect(() => {
    if (view !== 'onboarding' || !connectUrl) return
    const connectOrigin = new URL(connectUrl).origin
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== connectOrigin) return
      if ((event.data as { type?: string } | null)?.type !== 'remy:connector-connected') return
      connectPopupRef.current?.close()
      connectPopupRef.current = null
      recheckOnboarding()
    }
    window.addEventListener('message', onMessage)
    const timer = window.setInterval(() => {
      if (connectPopupRef.current?.closed) {
        connectPopupRef.current = null
        recheckOnboarding()
      }
    }, 1000)
    return () => {
      window.removeEventListener('message', onMessage)
      window.clearInterval(timer)
    }
  }, [view, connectUrl, recheckOnboarding])

  const resumeSession = useCallback(async (externalId: string) => {
    setResumingId(externalId)
    setLoadError(null)
    try {
      const history = await fetchRemySessionMessages(externalId)
      setInitialMessages(history)
      setSessionId(externalId)
      setView('chat')
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Couldn't load that conversation")
    } finally {
      setResumingId(null)
    }
  }, [])

  const browseHistory = useCallback(() => {
    setLoadError(null)
    setView('picker')
    // Refresh so the list reflects the conversation just had.
    fetchRemySessions(3)
      .then(({ sessions: list }) => setSessions(list))
      .catch(() => {})
  }, [])

  return (
    <>
      {createPortal(
        <>
          {view === 'loading' && (
            <S.Centered>
              <S.PanelText>Loading your conversations…</S.PanelText>
            </S.Centered>
          )}

          {view === 'onboarding' && connectUrl && (
            <OnboardingGate
              connectUrl={connectUrl}
              checking={checkingOnboarding}
              onConnect={openConnectPopup}
              onConnected={recheckOnboarding}
            />
          )}

          {view === 'gate' && (
            <SessionGate
              sessions={sessions}
              onContinue={() => setView('picker')}
              onNew={startNewChat}
            />
          )}

          {view === 'picker' && (
            <SessionPicker
              sessions={sessions}
              resumingId={resumingId}
              error={loadError}
              onPick={resumeSession}
              onNew={startNewChat}
            />
          )}

          {view === 'chat' && (
            <RemyChat
              key={sessionId}
              sessionId={sessionId}
              initialMessages={initialMessages}
              onNewChat={startNewChat}
              onBrowseHistory={browseHistory}
              canBrowseHistory={sessions.length > 0}
            />
          )}
        </>,
        chatHost,
      )}

      {open && (
        <AttachmentBar
          hasTransitionAnimation
          title={
            <S.TitleWithBadge>
              Ask Remy
              <BetaBadge />
            </S.TitleWithBadge>
          }
          width={PANEL_DEFAULT_WIDTH}
          resizable
          resizeMinWidth={PANEL_MIN_WIDTH}
          resizeMaxWidth={PANEL_MAX_WIDTH}
          resizeStorageKey="remyChat.panelWidth"
          onClose={onClose}
        >
          <S.ChatHostSlot ref={adoptChatHost} />
        </AttachmentBar>
      )}
    </>
  )
}
